import {
  Box,
  Button,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { mostrarFechaCompleta } from '../../utils/DateFunctions'
import Loading from '../Common/Loading'
import { useRef, useState } from 'react'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { HistorialDTO } from './AdminLocalState/AdminLocalState'
import DictionaryItem from '../../dto/DictionaryDTO'

export interface HistorialCardsProps {
  data: HistorialDTO[]
  loadingHistorial: boolean
  tiposCuenta: DictionaryItem[]
}

const HistorialCards = ({ data, loadingHistorial }: HistorialCardsProps) => {
  if (loadingHistorial) return <Loading />
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [registroCompleto, setRegistroCompleto] = useState<number | null>(null)
  const [shouldScroll, setShouldScroll] = useState<boolean>(false)
  const itemRefs = useRef<Array<HTMLDivElement | null>>([])
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const formarTabla = (headers: string[], rows: any, renderRow: (row: any) => React.ReactNode) => {
    const safeRows = Array.isArray(rows) ? rows : [rows]
    return (
      <Box sx={{ width: '100%' }}>
        <Table
          size="small"
          sx={{
            mt: 1,
            tableLayout: 'fixed',
            ...(isMobile && {
              minWidth: 300,
              '& td, & th': {
                p: 0.5,
                fontSize: '0.7rem'
              }
            })
          }}
        >
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: isMobile ? 'nowrap' : 'normal' }} key={header}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ maxWidth: '100%' }}>
            {safeRows.map((row, index) => (
              <TableRow key={index} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {renderRow(row)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    )
  }

  const formatValue = (value: any) => {
    // Arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return '—'

      if (typeof value[0] === 'object' && value[0] !== null) {
        const headers = Object.keys(value[0])

        return formarTabla(headers, value, (row) => (
          <>
            {headers.map((h) => (
              <TableCell key={h}>{String(row[h])}</TableCell>
            ))}
          </>
        ))
      }

      return value.join(', ')
    }

    if (typeof value === 'object' && value !== null) {
      const headers = Object.keys(value)

      return formarTabla(headers, [value], (row) => (
        <>
          {headers.map((h) => (
            <TableCell key={h}>{String(row[h])}</TableCell>
          ))}
        </>
      ))
    }
    return String(value)
  }

  const isEqual = (a: any, b: any): boolean => {
    if (a === b) return true

    if (typeof a !== typeof b) return false
    if (a === null || b === null) return false

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false
      return a.every((el, i) => isEqual(el, b[i]))
    }

    if (typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a)
      const keysB = Object.keys(b)
      if (keysA.length !== keysB.length) return false
      return keysA.every((k) => isEqual(a[k], b[k]))
    }

    return false
  }

  const getDiferencias = (actual: HistorialDTO['local'], next: HistorialDTO['local'] | null, mostrarTodo: boolean) => {
    return Object.entries(actual)
      .filter(([key, value]) => {
        if (value === null || value === undefined) return false
        if (!next || mostrarTodo) return true
        return !isEqual(value, (next as any)[key])
      })
      .map(([key, value]) => [key, formatValue(value)])
  }

  return (
    <Box>
      {data.map((item, index) => {
        const next = index < data.length - 1 ? data[index + 1].local : null
        const diferencias = getDiferencias(item.local, next, registroCompleto === index)
        return (
          <Box
            ref={(el: HTMLDivElement | null) => (itemRefs.current[index] = el)}
            sx={{ p: 1, m: 0.5, boxShadow: 1 }}
            key={item.id}
          >
            <Box>
              <Typography variant="body1">
                ID: <b>{item.id}</b>
              </Typography>
              <Typography variant="body1">
                ID Usuario: <b>{item.usuarioId}</b>
              </Typography>
              <Typography variant="body2">
                Fecha: <b>{mostrarFechaCompleta(item.timestamp)}</b>
              </Typography>
              <Typography variant="body2">Evento: {item.evento}</Typography>
              {item.borrado && (
                <Typography variant="body2" sx={{ color: 'red' }}>
                  Borrado
                </Typography>
              )}
              <List component="nav" disablePadding>
                <ListItemButton
                  sx={{ pl: 0 }}
                  onClick={() => {
                    setShouldScroll(openIndex !== null)
                    setOpenIndex(openIndex === index ? null : index)
                  }}
                >
                  <ListItemText primary={<Typography fontWeight="bold">Local</Typography>} />
                  {openIndex === index ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse
                  in={openIndex === index}
                  timeout="auto"
                  unmountOnExit
                  onEntering={() => {
                    const el = itemRefs.current[index]
                    if (el && shouldScroll) {
                      el.scrollIntoView({ block: 'start', behavior: 'auto' })
                    }
                  }}
                >
                  <List component="div" disablePadding sx={{ pl: isMobile ? 4 : 'auto' }}>
                    {diferencias.map(([field, value]) => (
                      <Box key={String(field)} sx={{ mb: 2 }}>
                        {typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? (
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: 'break-word',
                              whiteSpace: 'normal'
                            }}
                          >
                            <b>{field}:</b> {String(value)}
                          </Typography>
                        ) : (
                          <>
                            <Typography variant="body2" fontWeight="bold">
                              {field}:
                            </Typography>
                            {value}
                          </>
                        )}
                      </Box>
                    ))}
                  </List>
                  {index !== data.length - 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setRegistroCompleto(registroCompleto === index ? null : index)
                          registroCompleto === index &&
                            itemRefs.current[index]?.scrollIntoView({ block: 'start', behavior: 'auto' })
                        }}
                        sx={{
                          borderRadius: 1,
                          backgroundColor: '#616161',
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#757575'
                          }
                        }}
                      >
                        {registroCompleto === index ? 'Ver diferencias' : 'Registro completo'}
                      </Button>
                    </Box>
                  )}
                </Collapse>
              </List>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default HistorialCards
